<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import KanbanAPI from '../../../../api/kanban';
import Modal from '../../../../components/Modal.vue';
import Button from '../../../../components-next/button/Button.vue';
import Icon from '../../../../components-next/icon/Icon.vue';
import Select from '../../../../components-next/select/Select.vue';

const props = defineProps({
  currentFunnel: {
    type: Object,
    required: true,
  },
  funnels: {
    type: Array,
    default: () => [],
  },
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'items-imported']);

const store = useStore();

// Função para garantir que o valor é uma string
const ensureString = value => (value ? String(value) : '');

const step = ref('upload'); // 'upload', 'mapping', 'importing'
const file = ref(null);
const fileInput = ref(null);
const csvColumns = ref([]);
const previewRows = ref([]);
const totalRows = ref(0);
const loading = ref(false);
const error = ref(null);
const isModalOpen = ref(props.show);

// Computed para obter todos os funis
const funnels = computed(() => props.funnels || []);

watch(
  () => props.show,
  value => {
    isModalOpen.value = value;
  }
);

// Form data para funil e etapa
const importFormData = ref({
  funnel_id: ensureString(props.currentFunnel?.id || ''),
  stage_id: '',
});

// Computed para transformar os funis em options para o select
const funnelOptions = computed(() => {
  const options = funnels.value.map(funnel => ({
    value: String(funnel.id),
    label: funnel.name,
  }));

  // Se o funil atual não está na lista, adiciona ele
  if (importFormData.value.funnel_id) {
    const currentFunnelId = String(importFormData.value.funnel_id);
    const funnelExists = options.some(
      option => option.value === currentFunnelId
    );

    if (!funnelExists && props.currentFunnel) {
      options.unshift({
        value: currentFunnelId,
        label: props.currentFunnel.name || `Funil ${currentFunnelId}`,
      });
    }
  }

  return options;
});

// Computed para obter as etapas do funil selecionado
const funnelStages = computed(() => {
  const currentFunnel = funnels.value.find(
    f => String(f.id) === importFormData.value.funnel_id
  ) || props.currentFunnel;

  let stages = [];

  if (currentFunnel?.stages) {
    stages = Object.entries(currentFunnel.stages)
      .map(([id, stage]) => ({
        value: String(id),
        label: stage.name,
        position: stage.position,
      }))
      .sort((a, b) => a.position - b.position);
  }

  return stages;
});

// Computed para o v-model do funnel_id
const selectedFunnelId = computed({
  get: () => {
    return importFormData.value.funnel_id;
  },
  set: async value => {
    importFormData.value.funnel_id = ensureString(value);
    await handleFunnelChange(value);
  },
});

// Computed para o v-model do stage_id
const selectedStageId = computed({
  get: () => importFormData.value.stage_id,
  set: value => {
    importFormData.value.stage_id = ensureString(value);
  },
});

// Handler para quando um funil é selecionado
const handleFunnelChange = async value => {
  try {
    importFormData.value.funnel_id = ensureString(value);

    // Busca o funil completo dos records do store
    const selectedFunnelData = funnels.value.find(
      f => String(f.id) === importFormData.value.funnel_id
    ) || props.currentFunnel;

    if (selectedFunnelData) {
      // Atualiza o funil selecionado no store
      await store.dispatch('funnel/setSelectedFunnel', selectedFunnelData);

      await nextTick();

      // Verifica se a etapa atual existe no novo funil
      const stages = Object.keys(selectedFunnelData.stages || {});
      const currentStageId = importFormData.value.stage_id;
      const stageExists = stages.some(id => String(id) === currentStageId);

      // Limpa a etapa se não existir no novo funil
      if (!stageExists) {
        importFormData.value.stage_id = stages[0] || '';
      }
    }
  } catch (error) {
    console.error('Erro ao mudar funil:', error);
  }
};

// Campos mapeáveis do KanbanItem
const baseMappableFields = [
  { value: '', label: '-- Ignorar --' },
  { value: 'item_details.title', label: 'Título (obrigatório)' },
  { value: 'item_details.description', label: 'Descrição' },
  { value: 'item_details.status', label: 'Status' },
  { value: 'item_details.priority', label: 'Prioridade' },
  { value: 'item_details.value', label: 'Valor' },
  { value: 'item_details.customer_name', label: 'Nome do Cliente' },
  { value: 'item_details.customer_email', label: 'Email do Cliente' },
  { value: 'item_details.customer_phone', label: 'Telefone do Cliente' },
  { value: 'item_details.deadline_at', label: 'Data de Vencimento' },
      { value: 'funnel_stage', label: 'Etapa (opcional - use o seletor abaixo se não mapear)' },
  { value: 'assigned_agents', label: 'Agentes (separados por ; ou ,)' },
  { value: 'conversation_display_id', label: 'ID da Conversa' },
];

const selectedImportFunnel = computed(
  () => funnels.value.find(f => String(f.id) === importFormData.value.funnel_id) || props.currentFunnel
);

const mappableFields = computed(() => {
  const globalFields = (selectedImportFunnel.value?.global_custom_attributes || [])
    .filter(attribute => attribute?.name)
    .map(attribute => ({
      value: `global_custom_attribute:${attribute.name}`,
      label: `Campo global: ${attribute.name}`,
    }));

  const itemFields = csvColumns.value.map(column => ({
    value: `item_custom_attribute:${column}`,
    label: `Campo adicional do item: ${column}`,
  }));

  return [...baseMappableFields, ...globalFields, ...itemFields];
});

const visiblePreviewColumns = computed(() => csvColumns.value.slice(0, 10));
const hiddenPreviewColumns = computed(() => csvColumns.value.slice(10));

const requiredFieldChecklist = computed(() => [
  {
    label: 'Funil de destino',
    complete: Boolean(importFormData.value.funnel_id),
  },
  {
    label: 'Etapa de destino',
    complete: Boolean(importFormData.value.stage_id),
  },
  {
    label: 'Título do item',
    complete: Object.values(columnMappings.value).includes('item_details.title'),
  },
]);

const completedRequiredFields = computed(
  () => requiredFieldChecklist.value.filter(field => field.complete).length
);

// Mapeamentos de colunas CSV para campos do KanbanItem
const columnMappings = ref({});

// Estatísticas de importação
const importStats = ref(null);
const importPollingTimer = ref(null);

const handleFileSelect = async (event) => {
  const selectedFile = event.target.files?.[0];
  if (!selectedFile) return;

  if (!selectedFile.name.endsWith('.csv')) {
    error.value = 'Por favor, selecione um arquivo CSV';
    return;
  }

  file.value = selectedFile;
  error.value = null;
  loading.value = true;

  try {
    const response = await KanbanAPI.importPreview(selectedFile);
    csvColumns.value = response.data.columns || [];
    previewRows.value = response.data.preview_rows || [];
    totalRows.value = response.data.total_rows || 0;

    // Inicializar mapeamentos vazios
    columnMappings.value = {};
    csvColumns.value.forEach(column => {
      columnMappings.value[column] = '';
    });

    step.value = 'mapping';
  } catch (err) {
    error.value = err.response?.data?.error || 'Erro ao processar arquivo CSV';
    console.error('Erro ao processar CSV:', err);
  } finally {
    loading.value = false;
  }
};

const handleFileDrop = (event) => {
  event.preventDefault();
  const droppedFile = event.dataTransfer.files[0];
  if (droppedFile && droppedFile.name.endsWith('.csv')) {
    file.value = droppedFile;
    // Simular seleção de arquivo
    const mockEvent = { target: { files: [droppedFile] } };
    handleFileSelect(mockEvent);
  }
};

const handleDragOver = (event) => {
  event.preventDefault();
};

const handleImport = async () => {
  // Validar mapeamentos obrigatórios
  const hasTitle = Object.values(columnMappings.value).includes('item_details.title');
  
  if (!hasTitle) {
    error.value = 'É obrigatório mapear pelo menos o campo "Título"';
    return;
  }

  // Validar funil e etapa
  if (!importFormData.value.funnel_id) {
    error.value = 'É obrigatório selecionar um funil';
    return;
  }

  if (!importFormData.value.stage_id) {
    error.value = 'É obrigatório selecionar uma etapa';
    return;
  }

  loading.value = true;
  error.value = null;
  step.value = 'importing';

  try {
    const response = await KanbanAPI.importItems(
      importFormData.value.funnel_id,
      file.value,
      columnMappings.value,
      importFormData.value.stage_id
    );

    if (response.data.import_id) {
      handleClose();
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Erro ao importar itens';
    console.error('Erro ao importar:', err);
    step.value = 'mapping'; // Voltar para a etapa de mapeamento
  } finally {
    loading.value = false;
  }
};

const stopImportPolling = () => {
  if (importPollingTimer.value) {
    window.clearTimeout(importPollingTimer.value);
    importPollingTimer.value = null;
  }
};

const handleReset = () => {
  stopImportPolling();
  file.value = null;
  csvColumns.value = [];
  previewRows.value = [];
  columnMappings.value = {};
  error.value = null;
  importStats.value = null;
  step.value = 'upload';
  importFormData.value.stage_id = '';
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

// Inicializar o funil e etapa ao montar
onMounted(async () => {
  isModalOpen.value = true;

  try {
    // Se houver funis, buscar do store
    if (props.funnels?.length === 0) {
      await store.dispatch('funnel/fetch');
    }

    // Inicializar com o funil atual
    if (props.currentFunnel?.id) {
      importFormData.value.funnel_id = ensureString(props.currentFunnel.id);
      
      // Se o funil tem etapas, selecionar a primeira
      const stages = Object.keys(props.currentFunnel.stages || {});
      if (stages.length > 0) {
        importFormData.value.stage_id = stages[0];
      }

      // Atualizar o store
      await store.dispatch('funnel/setSelectedFunnel', props.currentFunnel);
    }
  } catch (error) {
    console.error('Erro ao inicializar formulário de importação:', error);
  }
});

onUnmounted(stopImportPolling);

const handleClose = () => {
  isModalOpen.value = false;
  handleReset();
  emit('close');
};

const handleModalClose = () => {
  handleReset();
  emit('close');
};

const isMappingValid = computed(() => {
  const mappings = Object.values(columnMappings.value);
  const hasTitle = mappings.includes('item_details.title');
  const hasFunnel = !!importFormData.value.funnel_id;
  const hasStage = !!importFormData.value.stage_id;
  return hasTitle && hasFunnel && hasStage;
});

const getFieldLabel = (fieldValue) => {
  const field = mappableFields.value.find(f => f.value === fieldValue);
  return field ? field.label : fieldValue;
};
</script>

<template>
  <Modal
    v-model:show="isModalOpen"
    size="full-width"
    :show-close-button="step !== 'importing'"
    :on-close="handleReset"
    @close="handleModalClose"
  >
    <div class="p-6 overflow-y-auto max-h-[calc(90vh-2rem)]">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <h3 class="text-base font-medium leading-6 text-n-slate-12">
          Importar Itens do CSV
        </h3>
      </div>

      <!-- Error Message -->
      <div
        v-if="error"
          class="mb-4 p-3 rounded-lg border border-n-ruby-9/30 bg-n-ruby-9/10 text-sm text-n-ruby-11"
      >
        {{ error }}
      </div>

      <!-- Step 1: Upload -->
      <div v-if="step === 'upload'">
        <div
          class="cursor-pointer rounded-xl border border-dashed border-n-weak p-8 text-center transition-colors hover:border-n-brand hover:bg-n-alpha-2"
          @drop="handleFileDrop"
          @dragover="handleDragOver"
          @click="fileInput?.click()"
        >
          <input
            ref="fileInput"
            type="file"
            accept=".csv"
            class="hidden"
            @change="handleFileSelect"
          />
          <div class="mb-4 flex justify-center text-n-slate-10">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file-text-icon lucide-file-text">
              <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"/>
              <path d="M14 2v5a1 1 0 0 0 1 1h5"/>
              <path d="M10 9H8"/>
              <path d="M16 13H8"/>
              <path d="M16 17H8"/>
            </svg>
          </div>
          <p class="mb-2 text-sm text-n-slate-11">
            Clique para selecionar ou arraste um arquivo CSV aqui
          </p>
          <p class="text-xs text-n-slate-10">
            Formato aceito: .csv
          </p>
        </div>
      </div>

      <!-- Step 2: Mapping -->
      <div v-if="step === 'mapping'">
        <div class="mb-4">
          <p class="mb-2 text-sm text-n-slate-11">
            Arquivo: <strong>{{ file?.name }}</strong> ({{ totalRows }} linhas)
          </p>
          <p class="mb-4 text-sm text-n-slate-11">
            Mapeie as colunas do CSV para os campos do sistema:
          </p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 lg:gap-x-6 items-start">
          <!-- Required fields -->
          <div class="mb-4 rounded-xl border border-n-weak bg-n-surface-2 p-3.5 lg:col-start-1 lg:row-start-1">
            <div class="flex items-start justify-between gap-3">
              <div>
                <h4 class="text-sm font-medium text-n-slate-12">
                  Campos obrigatórios
                </h4>
                <p class="mt-0.5 text-xs text-n-slate-10">
                  Complete estes itens para importar
                </p>
              </div>
              <span class="shrink-0 rounded-md bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-11">
                {{ completedRequiredFields }}/{{ requiredFieldChecklist.length }}
              </span>
            </div>
            <ul class="mt-3 space-y-2">
              <li
                v-for="field in requiredFieldChecklist"
                :key="field.label"
                class="flex items-center gap-2 text-sm"
                :class="field.complete ? 'text-n-teal-11' : 'text-n-slate-11'"
              >
                <Icon
                  :icon="field.complete ? 'i-lucide-circle-check' : 'i-lucide-circle'"
                  class="size-4 shrink-0"
                />
                <span>{{ field.label }}</span>
                <span v-if="field.complete" class="sr-only">Concluído</span>
              </li>
            </ul>
          </div>

          <!-- Seletores de Funil e Etapa -->
          <div class="mb-6 rounded-xl border border-n-weak bg-n-surface-1 p-3.5 lg:col-start-1 lg:row-start-2">
          <h4 class="mb-4 text-sm font-medium text-n-slate-12">
            Funil e Etapa de Destino
          </h4>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label
                class="mb-1.5 block text-xs font-medium text-n-slate-11"
              >
                Funil
              </label>
              <Select
                v-model="selectedFunnelId"
                :options="funnelOptions"
                placeholder="Selecione um funil"
                class="w-full [&_select]:w-full"
              />
            </div>

            <div>
              <label
                class="mb-1.5 block text-xs font-medium text-n-slate-11"
              >
                Etapa
              </label>
              <Select
                v-model="selectedStageId"
                :options="funnelStages"
                placeholder="Selecione uma etapa"
                :disabled="!importFormData.funnel_id"
                class="w-full [&_select]:w-full"
              />
            </div>
          </div>
          </div>

          <!-- Preview Table -->
          <div class="mb-6 min-w-0 overflow-hidden rounded-xl border border-n-weak bg-n-surface-1 lg:col-start-2 lg:row-start-1 lg:row-span-3">
            <div class="flex items-center justify-between gap-3 border-b border-n-weak px-3.5 py-3">
              <div class="min-w-0">
                <h4 class="truncate text-sm font-medium text-n-slate-12">
                  Preview do CSV
                </h4>
                <p class="mt-0.5 text-xs text-n-slate-10">
                  Primeiras linhas do arquivo
                </p>
              </div>
              <span class="shrink-0 rounded-md bg-n-slate-3 px-2 py-1 text-xs text-n-slate-11">
                {{ previewRows.length }} linhas
              </span>
            </div>
            <div class="max-h-[26rem] overflow-x-auto overflow-y-hidden">
              <table class="w-full min-w-[56rem] table-fixed text-xs">
                <thead class="sticky top-0 z-10 bg-n-surface-2">
                  <tr>
                    <th class="w-10 border-b border-n-weak px-3 py-2 text-left font-medium text-n-slate-10">
                      #
                    </th>
                    <th
                      v-for="column in visiblePreviewColumns"
                      :key="column"
                      :title="column"
                      class="max-w-[14rem] overflow-hidden text-ellipsis whitespace-nowrap border-b border-n-weak px-3 py-2 text-left font-medium text-n-slate-11"
                    >
                      {{ column }}
                    </th>
                    <th
                      v-if="hiddenPreviewColumns.length"
                      :title="hiddenPreviewColumns.join(', ')"
                      class="w-32 cursor-help whitespace-nowrap border-b border-n-weak px-3 py-2 text-left font-medium text-n-slate-11"
                    >
                      +{{ hiddenPreviewColumns.length }} colunas
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak">
                  <tr
                    v-for="(row, index) in previewRows"
                    :key="index"
                    class="hover:bg-n-alpha-2"
                  >
                    <td class="px-3 py-2 text-n-slate-10">
                      {{ index + 1 }}
                    </td>
                    <td
                      v-for="column in visiblePreviewColumns"
                      :key="column"
                      :title="row[column] || '-'"
                      class="max-w-[14rem] truncate whitespace-nowrap px-3 py-2 text-n-slate-11"
                    >
                      {{ row[column] || '-' }}
                    </td>
                    <td
                      v-if="hiddenPreviewColumns.length"
                      class="px-3 py-2 text-n-slate-10"
                    >
                      ...
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Column Mappings -->
          <div class="grid grid-cols-1 gap-y-3 mb-6 lg:col-start-1 lg:row-start-3">
          <div
            v-for="column in csvColumns"
            :key="column"
            class="grid grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)] items-center gap-x-3 gap-y-1 rounded-lg border border-n-weak bg-n-surface-1 p-3"
          >
            <label class="break-words text-sm font-medium text-n-slate-12">
              {{ column }}:
            </label>
            <Select
              v-model="columnMappings[column]"
              :options="mappableFields"
              class="w-full [&_select]:w-full [&_select]:min-w-0"
            />
            <span
              v-if="columnMappings[column]"
              class="col-span-2 truncate text-xs text-n-slate-10"
            >
              → {{ getFieldLabel(columnMappings[column]) }}
            </span>
          </div>
          </div>
        </div>

        <div class="sticky bottom-0 z-20 -mx-6 mt-6 flex justify-end gap-2 border-t border-n-weak bg-n-surface-1/95 px-6 pt-3 backdrop-blur-sm">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            @click="handleReset"
          >
            Voltar
          </Button>
          <Button
            variant="solid"
            color="blue"
            size="sm"
            :isLoading="loading"
            :disabled="!isMappingValid"
            @click="handleImport"
          >
            Importar {{ totalRows }} itens
          </Button>
        </div>
      </div>

      <!-- Step 3: Importing/Results -->
      <div v-if="step === 'importing'">
        <div v-if="loading" class="text-center py-8">
          <div class="inline-block mb-4 animate-spin">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-history-icon lucide-history text-n-slate-10">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
              <path d="M3 3v5h5"/>
              <path d="M12 7v5l4 2"/>
            </svg>
          </div>
          <p class="text-sm text-n-slate-11">
            Importando itens...
          </p>
        </div>

        <div v-else-if="importStats" class="space-y-4">
          <div class="rounded-lg border border-n-teal-9/30 bg-n-teal-9/10 p-4">
            <h4 class="mb-2 font-medium text-n-teal-11">
              Importação concluída!
            </h4>
            <div class="space-y-1 text-sm text-n-teal-11">
              <p>✅ {{ importStats.created_count }} itens importados com sucesso</p>
              <p v-if="importStats.error_count > 0">
                ⚠️ {{ importStats.error_count }} itens com erro
              </p>
              <p>Total processado: {{ importStats.total_rows }} linhas</p>
            </div>
          </div>

          <!-- Errors List -->
          <div
            v-if="importStats.errors && importStats.errors.length > 0"
            class="max-h-64 overflow-y-auto rounded-lg border border-n-ruby-9/30 bg-n-ruby-9/10 p-4"
          >
            <h4 class="mb-2 font-medium text-n-ruby-11">
              Erros encontrados:
            </h4>
            <ul class="space-y-1 text-xs text-n-ruby-11">
              <li
                v-for="(err, index) in importStats.errors"
                :key="index"
              >
                Linha {{ err.row }}: {{ err.error }}
              </li>
            </ul>
          </div>

          <div class="sticky bottom-0 z-20 -mx-6 mt-6 flex justify-end gap-2 border-t border-n-weak bg-n-surface-1/95 px-6 pt-3 backdrop-blur-sm">
            <Button
              variant="solid"
              color="blue"
              size="sm"
              @click="handleClose"
            >
              Fechar
            </Button>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>
